/* eslint-disable complexity */
/* eslint-disable max-statements */
import * as R from "ramda"
import {WIDGET_TYPES} from "widgets/constants"

const TAG_DELIMITER = "|"
const TAG_PROPERTY_DELIMITER = ";"

const getTargetAttributesAsString = target => {
  const attributesAllowlist = [
    "data-heap-id",
    "data-heap-bank-name",
    "data-heap-is-popular-banks-list",
    "href",
    "variant",
  ]
  const attributes = target
    .getAttributeNames()
    .filter(name => attributesAllowlist.includes(name))

  const getAttributeString = attribute =>
    `[${attribute}="${target.getAttribute(
      attribute
    )}"]${TAG_PROPERTY_DELIMITER}`

  const attributesString = attributes.map(getAttributeString).join("")
  return attributesString
}

const getCurrentTargetInfo = target => {
  const initialTargetInfoAsString = `@${target.tagName.toLowerCase()}${TAG_PROPERTY_DELIMITER}`

  const appendTargetId = R.concat(
    R.__,
    `#${target.id}${TAG_PROPERTY_DELIMITER}`
  )

  const classes = Array.from(target.classList || [])
  const classesJoinSeparator = `${TAG_PROPERTY_DELIMITER}.`
  const appendClasses = R.concat(
    R.__,
    `.${classes.join(classesJoinSeparator)}${TAG_PROPERTY_DELIMITER}`
  )

  const attributesString = getTargetAttributesAsString(target)
  const appendAttributes = R.concat(R.__, attributesString)

  return R.pipe(
    R.when(() => target.id, appendTargetId),
    R.when(() => classes.length > 0, appendClasses),
    appendAttributes
  )(initialTargetInfoAsString)
}

const getHierarchyAsArray = target => {
  const tags = []
  let currentTarget = target

  while (currentTarget && currentTarget.tagName?.toLowerCase() !== "body") {
    const currentTargetInfoAsString = getCurrentTargetInfo(currentTarget)
    tags.unshift(currentTargetInfoAsString)
    if (currentTarget.parentElement) {
      currentTarget = currentTarget.parentElement
    } else {
      currentTarget = currentTarget.getRootNode().host
    }
  }

  return tags
}

const getHierarchy = target => {
  const tags = getHierarchyAsArray(target)

  const fullHierarchy = tags.join(TAG_DELIMITER)

  if (fullHierarchy.length <= 1024) {
    return fullHierarchy
  }
  return fullHierarchy
    .slice(-1024)
    .split(TAG_DELIMITER)
    .slice(1)
    .join(TAG_DELIMITER)
}

const eventNames = ["click"]
const useCapture = true
const trackOnlyShadowDom = true
const mhHeapScriptId = "mh-heap-script"
const heapScriptId = "heap-script"
const widgetWebComponentName = "mh-widget"

const getFullPageHash = getWindow => {
  return getWindow().location.hash
}

const getPage = getWindow => {
  return getFullPageHash(getWindow).replace("#/", "").split("/")[0]
}

const getHeapIdSuffix = ({heapId, nearestHeapBankName, nearestHeapId}) => {
  if (heapId) {
    return ` - ${heapId}`
  }
  if (nearestHeapBankName) {
    return " - bank"
  }
  if (nearestHeapId) {
    return ` - ${nearestHeapId}`
  }
  return ""
}

const getAttributeValue = (targetElement, dataAttribute) => {
  return targetElement.getAttribute(dataAttribute)
}

const getParentAttributeValue = (targetElement, dataAttribute) => {
  if (targetElement.parentElement) {
    return getAttributeValue(targetElement.parentElement, dataAttribute)
  }
  return null
}

const getNearestAttributeValue = (targetElement, dataAttribute) => {
  const attributeValue = getAttributeValue(targetElement, dataAttribute)
  if (attributeValue) {
    return attributeValue
  } else if (targetElement.parentElement) {
    return getNearestAttributeValue(targetElement.parentElement, dataAttribute)
  }
  return null
}

const callback =
  (getWindow, additionalHeapEventProperties = {}) =>
  event => {
    if ("composed" in event && typeof event.composedPath === "function") {
      // Get the path of elements the event climbed through, e.g.
      // [span, div, div, section, body]
      const path = event.composedPath()

      // Fetch reference to the element that was actually clicked
      const targetElement = path[0]
      // Create the Heap-style hierarchy
      const hierarchy = getHierarchy(targetElement)
      // Check if the element is WITHIN the shadow DOM (ignoring the root)
      const shadowFound = path.length
        ? path.filter(function (i) {
            return !targetElement.shadowRoot && !!i.shadowRoot
          }).length > 0
        : false

      // If only shadow DOM events should be tracked and the element is not within one, return
      if (trackOnlyShadowDom && !shadowFound) return

      const heapId = getAttributeValue(targetElement, "data-heap-id")
      const parentHeapId = getParentAttributeValue(
        targetElement,
        "data-heap-id"
      )
      const nearestHeapId = getNearestAttributeValue(
        targetElement,
        "data-heap-id"
      )
      const page = getPage(getWindow)
      const pageHash = getFullPageHash(getWindow)
      const nearestHeapBankName = getNearestAttributeValue(
        targetElement,
        "data-heap-bank-name"
      )
      const isPopularBanksList = getNearestAttributeValue(
        targetElement,
        "data-heap-is-popular-banks-list"
      )

      const heapIdSuffix = getHeapIdSuffix({
        heapId,
        nearestHeapId,
        nearestHeapBankName,
      })
      const eventName = `${page} - ${event.type}${heapIdSuffix}`

      // Send to Heap
      const properties = {
        eventName,
        shadowHierarchy: hierarchy,
        shadowTargetText:
          targetElement.innerText || targetElement.textContent || null,
        shadowHref: targetElement.href || targetElement.action || null,
        inShadowDom: shadowFound,
        heapId: heapId || "none", // Directly on the target element
        parentHeapId,
        nearestHeapId,
        page,
        pageHash,
        nearestHeapBankName,
        isPopularBanksList,
        ...additionalHeapEventProperties,
      }
      const {heap} = getWindow() // Need to pass in a function instead of window directly due to this issue: https://github.com/segmentio/analytics.js/issues/605#issuecomment-605464507
      heap.track(eventName, properties)
    }
  }

// Workaround to capturing shadow DOM events adapted from https://community.heap.io/defining-events-and-properties-40/tracking-events-in-web-components-shadow-dom-262
const addHeapShadowDomEventListeners = (
  getWindow,
  additionalHeapEventProperties
) => {
  eventNames.forEach(eventName => {
    getWindow()
      .document.querySelector(widgetWebComponentName)
      .addEventListener(
        eventName,
        callback(getWindow, additionalHeapEventProperties),
        useCapture
      )
  })
}

const removeHeapScriptsAndDomEntry = getWindow => {
  const window = getWindow()

  // Ensures heap can't be re-loaded and setup again by accident
  const mhHeapScript = window.document.querySelector(
    `[data-id="${mhHeapScriptId}"]`
  )
  if (mhHeapScript) {
    mhHeapScript.remove()
  }
  const heapScript = window.document.querySelector(
    `[data-id="${heapScriptId}"]`
  )
  if (heapScript) {
    heapScript.remove()
  }

  // Override heap functions to prevent errors when script is still loaded but we don't want to track anymore( e.g. non-widget pages)
  if (window.heap) {
    window.heap = {
      track() {},
      identify() {},
    }
  }
}

const removeHeap = getWindow => {
  removeHeapScriptsAndDomEntry(getWindow)
}

const identifyUserByExternalId = ({
  getWindow,
  externalUserId,
  externalReferenceId,
}) => {
  if (externalUserId && externalReferenceId) {
    getWindow().heap.identify(`${externalReferenceId} - ${externalUserId}`)
  } else if (externalUserId) {
    getWindow().heap.identify(externalUserId)
  } else if (externalReferenceId) {
    getWindow().heap.identify(externalReferenceId)
  }
}

// Widget ID -> Heap Environment ID
const affordabilityHeapEnvironmentLookupByWidgetId = {
  "43826156-f431-4121-b744-f9d6084d0a7a": "560760160", // Don't label these
  "42c88a35-cd82-47d8-aee8-680a76c1fc7f": "245128944",
  "a76a6aa7-0813-41b9-b962-f19b3fded9be": "263487074",
  "c5b96bde-99b3-4b95-96cb-b62a78f768e1": "4102605715",
  "08a89397-518e-4aa8-b7ab-5ee1ae8f7f95": "4102605715",
  "4498dcf2-834f-4752-a793-a596ac89df20": "3552098105",
  "db45b686-1edd-4724-b917-2e5d7835de71": "560760160",
  "adc7e361-1817-42c0-9e4e-16953bc325bb": "1342982756",
  "09b90555-7b92-40f0-85c2-1260c98107b0": "3446073612",
  "fbbc84bb-f94c-4e25-8432-1f69bfa43692": "2403626968",
  "f321d388-f3d3-4a5b-ade1-eb625dcf1904": "876294745",
  "a9042d56-c035-407b-96b1-36b97941101d": "1825449384",
  "b475290b-4d00-4f92-b2c3-2209177a354a": "4187053454",
}
const standardFinancialStatementHeapEnvironmentLookupByWidgetId = {
  // Will be populated when we have production widgets
}
const accountInformationHeapEnvironmentLookupByWidgetId = {
  "f3d09edd-ff72-487b-897f-3e3fbba8ad6a": "3769428751",
  "0216a481-6174-4393-bb78-a335750a0a77": "3769428751",
}
const collectionsLiteHeapEnvironmentLookupByWidgetId = {
  "f89c1a2a-586a-440b-b4b9-391680875d8b": "363774566",
}

const getHeapEnvironmentLookup = ({widgetType}) => {
  if (widgetType === WIDGET_TYPES.AFFORDABILITY) {
    return affordabilityHeapEnvironmentLookupByWidgetId
  }
  if (widgetType === WIDGET_TYPES.STANDARD_FINANCIAL_STATEMENT) {
    return standardFinancialStatementHeapEnvironmentLookupByWidgetId
  }
  if (widgetType === WIDGET_TYPES.COLLECTIONS_LITE) {
    return collectionsLiteHeapEnvironmentLookupByWidgetId
  }
  if (widgetType === WIDGET_TYPES.ACCOUNT_INFORMATION) {
    return accountInformationHeapEnvironmentLookupByWidgetId
  }
  return {}
}

const heapDevelopmentEnvironmentLookupByType = {
  [WIDGET_TYPES.AFFORDABILITY]: "245128944",
  [WIDGET_TYPES.STANDARD_FINANCIAL_STATEMENT]: "71349139",
  [WIDGET_TYPES.COLLECTIONS_LITE]: "3059477350",
  [WIDGET_TYPES.ACCOUNT_INFORMATION]: "310577962",
}

const getHeapEnvironmentId = ({widgetId, widgetType}) => {
  const heapEnvironmentLookup = getHeapEnvironmentLookup({widgetType})
  const heapEnvironmentId = heapEnvironmentLookup[widgetId]

  if (heapEnvironmentId) {
    return heapEnvironmentId
  }
  const captureLocalWidgetEventsInHeapDevEnvironment =
    process.env.REACT_APP_ENABLE_LOCAL_HEAP_DATA_CAPTURE || false

  const devEnvironmentHostnames = [
    "identity-dev.moneyhub.co.uk",
    "hosted-widgets-test.vercel.app",
  ]

  if (
    devEnvironmentHostnames.includes(window.location.hostname) ||
    captureLocalWidgetEventsInHeapDevEnvironment
  ) {
    // Heap "Development" environment for the widget type
    return heapDevelopmentEnvironmentLookupByType[widgetType]
  }

  return null
}

const addHeapScript = heapEnvironmentId => {
  const existingScript = document.querySelector(`[data-id="${mhHeapScriptId}"]`)
  if (existingScript) {
    return
  }
  const script = document.createElement("script")
  script.setAttribute("data-id", `${mhHeapScriptId}`)
  script.type = "text/javascript"

  // TODO move to only using new heap SDK once tested and verified in prod on hosted sites
  const useNewHeapSDK = false

  if (useNewHeapSDK) {
    // This is taken from the heap docs, apart from setting the data-id attribute to heap-script - needed as part of removing heap on unmount
    script.innerHTML = `window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";a.setAttribute("data-id", "${heapScriptId}");var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
    heap.load("${heapEnvironmentId}");`
  } else {
    // Old Heap SDK script
    script.innerHTML = `window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.setAttribute("data-id", "${heapScriptId}");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
    heap.load(${heapEnvironmentId});`
  }

  window.document.head.appendChild(script)
}

const setupHeap = ({
  getWindow,
  additionalHeapEventProperties,
  heapEnvironmentId,
  externalUserId,
  externalReferenceId,
}) => {
  addHeapScript(heapEnvironmentId)
  identifyUserByExternalId({getWindow, externalUserId, externalReferenceId})
  addHeapShadowDomEventListeners(getWindow, additionalHeapEventProperties)
}

const setupHeapByWidgetId = ({
  getWindow,
  additionalHeapEventProperties,
  widgetId,
  externalUserId,
  externalReferenceId,
  widgetType,
}) => {
  const heapEnvironmentId = getHeapEnvironmentId({widgetId, widgetType})
  if (heapEnvironmentId) {
    setupHeap({
      getWindow,
      additionalHeapEventProperties,
      heapEnvironmentId,
      externalUserId,
      externalReferenceId,
    })
  }
}

const setHeapIdentity = ({getWindow, externalUserId, externalReferenceId}) => {
  if (!getWindow()?.heap) return

  identifyUserByExternalId({getWindow, externalUserId, externalReferenceId})
}

const getFullErrorString = ({error}) => {
  if (!error) {
    return undefined
  }
  if (typeof error === "string") {
    return error
  }
  if (error instanceof Error) {
    return error.toString()
  }
  return JSON.stringify(error)
}

const getErrorEventLabel = ({error}) => {
  const message = R.prop("message", error)
  if (message) {
    return message
  }

  const name = R.prop("name", error)
  if (name) {
    return name
  }

  const dataMessage = R.path(["data", "message"], error)
  if (dataMessage) {
    return dataMessage
  }

  const dataName = R.path(["data", "name"], error)
  if (dataName) {
    return dataName
  }

  const UNKNOWN_ERROR_LABEL = "Unknown error"

  if (typeof error === "object") {
    // Don't want large stringified objects as event labels - we can store this separately
    return UNKNOWN_ERROR_LABEL
  }

  const trimmedError = error?.toString().trim()
  if (trimmedError) {
    return trimmedError
  }
  return UNKNOWN_ERROR_LABEL
}

const getEventProperties = ({properties}) => {
  const error = R.prop("error", properties)
  if (error) {
    const errorEventLabel = getErrorEventLabel({error})
    const fullError = getFullErrorString({error})
    return {
      ...properties,
      error: errorEventLabel,
      fullError,
    }
  }
  return properties
}

const getHeapLoggerEventName = ({message, properties = {}}) => {
  const {heapEventName} = properties
  if (heapEventName) {
    return heapEventName
  }
  return message
}

const logEventWithHeap = ({getWindow, message, properties}) => {
  const heap = getWindow().heap
  if (heap) {
    const heapEventName = getHeapLoggerEventName({message, properties})
    const eventProperties = getEventProperties({
      properties,
    })
    heap.track(heapEventName, eventProperties)
  }
}

const wrapLogger = ({
  getWindow,
  logger: hostingSiteLogger,
  additionalHeapEventProperties = {},
}) => {
  const dontLogIncomeThreshold = R.pipe(
    R.dissocPath(["params", "incomeThreshold"]),
    R.dissocPath(["props", "params", "incomeThreshold"])
  )

  const getLoggerProperties = R.pipe(
    R.omit(["heapEventName", "userId"]),
    dontLogIncomeThreshold
  )
  const getHeapProperties = R.pipe(
    R.mergeLeft(additionalHeapEventProperties),
    dontLogIncomeThreshold
  )
  const heapLogger = {
    info: (message, properties) => {
      const loggerProperties = getLoggerProperties(properties)
      hostingSiteLogger?.info(message, loggerProperties)

      const heapProperties = getHeapProperties(properties)
      logEventWithHeap({
        getWindow,
        message,
        properties: heapProperties,
      })
    },
    error: (message, properties) => {
      const loggerProperties = getLoggerProperties(properties)
      hostingSiteLogger?.error(message, loggerProperties)

      const heapProperties = getHeapProperties(properties)
      logEventWithHeap({
        getWindow,
        message,
        properties: heapProperties,
      })
    },
  }
  return heapLogger
}

const getManualHeapEventName = ({
  page = "page",
  eventType = "info",
  eventLabel,
}) => {
  if (eventLabel) {
    return `${page} - ${eventType} - ${eventLabel}`
  }
  return `${page} - ${eventType}`
}

const getManualHeapErrorEventName = ({page}) => {
  const eventType = "error"
  return getManualHeapEventName({page, eventType})
}

const forTesting = {
  getTargetAttributesAsString,
  getCurrentTargetInfo,
  getHierarchyAsArray,
  getHierarchy,
  getFullPageHash,
  getPage,
  getHeapIdSuffix,
  getHeapLoggerEventName,
  logEventWithHeap,
  getErrorEventLabel,
  getFullErrorString,
  getEventProperties,
  callback,
  getAttributeValue,
  getParentAttributeValue,
  getNearestAttributeValue,
}

export {
  getManualHeapErrorEventName,
  getManualHeapEventName,
  removeHeap,
  setHeapIdentity,
  setupHeap,
  setupHeapByWidgetId,
  wrapLogger,
  forTesting,
}
